import type { FeatureFlags } from '@/types';

export type PlanTier = 'FREE' | 'STARTER' | 'GROWTH' | 'ENTERPRISE';
export interface PlanLimits {
  products: number;
  staff: number;
  ordersPerMonth: number;
}
export interface PlanDef {
  id: string;
  name: string;
  tier: PlanTier;
  priceMonthly: number;
  priceYearly: number;
  description: string;
  limits: PlanLimits;
  features: FeatureFlags;
}

const flags = (on: Partial<FeatureFlags>): FeatureFlags => ({
  products: true, categories: true, cart: true, orders: true, payments: true,
  wallet: false, loyalty: false, coupons: false, banners: false, reviews: false,
  referrals: false, affiliate: false, campaigns: false, analytics: false,
  ...on,
});

/**
 * Starting catalogue of platform plans. This is the seed only — the live, editable catalogue lives in the
 * Super Admin mock store (`services/mock/platform/context.ts`) and is what tenants actually resolve against.
 * `referrals` is intentionally always false: it is a legacy key superseded by `affiliate` (see featureRegistry.ts).
 */
export const PLAN_DEFS: PlanDef[] = [
  {
    id: 'plan_free', name: 'Free', tier: 'FREE', priceMonthly: 0, priceYearly: 0,
    description: 'Get a store online with the commerce essentials.',
    limits: { products: 20, staff: 1, ordersPerMonth: 100 },
    features: flags({}),
  },
  {
    id: 'plan_starter', name: 'Starter', tier: 'STARTER', priceMonthly: 999, priceYearly: 9990,
    description: 'Coupons, banners and reviews to start growing.',
    limits: { products: 200, staff: 3, ordersPerMonth: 1000 },
    features: flags({ coupons: true, banners: true, reviews: true }),
  },
  {
    id: 'plan_growth', name: 'Growth', tier: 'GROWTH', priceMonthly: 2999, priceYearly: 29990,
    description: 'Wallet, loyalty and analytics for scaling stores.',
    limits: { products: 2000, staff: 10, ordersPerMonth: 10000 },
    features: flags({ coupons: true, banners: true, reviews: true, wallet: true, loyalty: true, analytics: true }),
  },
  {
    id: 'plan_enterprise', name: 'Enterprise', tier: 'ENTERPRISE', priceMonthly: 9999, priceYearly: 99990,
    description: 'Everything, including affiliate and marketing campaigns.',
    limits: { products: 999999, staff: 999, ordersPerMonth: 999999 },
    features: flags({ coupons: true, banners: true, reviews: true, wallet: true, loyalty: true, analytics: true, affiliate: true, campaigns: true }),
  },
];

export const planById = (id: string) => PLAN_DEFS.find((p) => p.id === id);
